/**
 * Task 151-B — shared primitives for the per-service config sections:
 * a toggle switch row, a multi-select chip grid, a centered number
 * field, and a small section title. Built on the existing V2 tokens
 * (`.chip` / `.chip.on`, `.auth-field` / `.auth-input`, CSS vars) — no
 * new design system. Presentation only.
 */

import type { ReactNode } from 'react';

export function SectionTitle({ children }: { readonly children: ReactNode }) {
  return (
    <div
      style={{
        paddingBottom: 8,
        borderBottom: '1px solid var(--line)',
        fontSize: 13,
        fontWeight: 700,
        color: 'var(--ink)',
      }}
    >
      {children}
    </div>
  );
}

export function ToggleRow({
  label,
  desc,
  value,
  onChange,
  disabled,
}: {
  readonly label: string;
  readonly desc?: string;
  readonly value: boolean;
  readonly onChange: (next: boolean) => void;
  readonly disabled?: boolean;
}) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 12,
        padding: '10px 12px',
        background: 'var(--cream-2)',
        borderRadius: 'var(--r-md)',
      }}
    >
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 600 }}>{label}</div>
        {desc ? (
          <div className="tiny" style={{ color: 'var(--muted)' }}>
            {desc}
          </div>
        ) : null}
      </div>
      <button
        type="button"
        role="switch"
        aria-checked={value}
        aria-label={label}
        disabled={disabled}
        onClick={() => onChange(!value)}
        style={{
          width: 38,
          height: 22,
          borderRadius: 'var(--r-full)',
          border: 'none',
          flexShrink: 0,
          cursor: disabled ? 'not-allowed' : 'pointer',
          background: value ? 'var(--tangerine)' : 'var(--line-3)',
          position: 'relative',
          transition: 'background .15s',
        }}
      >
        <span
          style={{
            position: 'absolute',
            top: 2,
            insetInlineStart: value ? 2 : 'auto',
            insetInlineEnd: value ? 'auto' : 2,
            width: 18,
            height: 18,
            borderRadius: '50%',
            background: '#fff',
          }}
        />
      </button>
    </div>
  );
}

export function ChipGrid<T extends string>({
  items,
  selected,
  onToggle,
  disabled,
}: {
  readonly items: ReadonlyArray<{ readonly key: T; readonly label: string }>;
  readonly selected: ReadonlyArray<T>;
  readonly onToggle: (key: T) => void;
  readonly disabled?: boolean;
}) {
  return (
    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
      {items.map((it) => {
        const on = selected.includes(it.key);
        return (
          <button
            key={it.key}
            type="button"
            className={`chip${on ? ' on' : ''}`}
            aria-pressed={on}
            disabled={disabled}
            onClick={() => onToggle(it.key)}
          >
            {on ? '✓ ' : ''}
            {it.label}
          </button>
        );
      })}
    </div>
  );
}

export function NumberField({
  label,
  value,
  onChange,
  step = 1,
  min = 0,
  disabled,
}: {
  readonly label: string;
  readonly value: number;
  readonly onChange: (next: number) => void;
  readonly step?: number;
  readonly min?: number;
  readonly disabled?: boolean;
}) {
  return (
    <div className="auth-field" style={{ marginBottom: 0 }}>
      <label className="auth-field-label">{label}</label>
      <input
        className="auth-input"
        type="number"
        inputMode="decimal"
        step={step}
        min={min}
        value={Number.isFinite(value) ? value : min}
        disabled={disabled}
        onChange={(e) => {
          const n = parseFloat(e.currentTarget.value);
          onChange(Number.isFinite(n) ? (n < min ? min : n) : min);
        }}
        style={{ paddingInlineStart: 18, textAlign: 'center' }}
      />
    </div>
  );
}
